import { TrackingService } from '../services/TrackingService'
import { FieldInputted } from '../events/FieldInputted'
import type { FormContext, StepContext, UserInputField } from '../types'

export type FieldInputtedConfig = {
  formContext?: FormContext
  stepContext?: StepContext
  userInputField: UserInputField
}

/**
 * Hook for tracking form field input events
 *
 * Tracks when a user inputs data into a form field. This is useful for
 * understanding user interaction with forms and identifying potential
 * friction points in the user journey.
 *
 * @returns A function that tracks field input events
 *
 * @example
 * ```typescript
 * const trackFieldInputted = useFieldInputted()
 *
 * const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *   trackFieldInputted({
 *     fieldLabel: 'Email Address',
 *     fieldName: 'email',
 *     fieldType: 'email',
 *     fieldValue: e.target.value
 *   })
 * }
 * ```
 */
function useFieldInputted(): (params: FieldInputtedConfig) => void {
  return function trackFieldInputted(params: FieldInputtedConfig): void {
    const fieldInputtedEvent = new FieldInputted({
      formContext: params.formContext,
      stepContext: params.stepContext,
      userInputField: params.userInputField,
    })

    TrackingService.track(fieldInputtedEvent)
  }
}

export default useFieldInputted
